import { create } from "zustand";

const TRUSTED_SITES_KEY = "avacus:trusted-link-hosts:v1";

interface LinkSafetyState {
  pendingUrl: string | null;
  trustedHosts: string[];
  blockedHosts: string[];
}

export const useLinkSafetyStore = create<LinkSafetyState>(() => ({
  pendingUrl: null,
  trustedHosts: [],
  blockedHosts: [],
}));

export const parseWebsiteUrl = (value: string): URL | null => {
  try {
    if (typeof value !== "string" || !value.trim()) return null;
    const url = new URL(value, window.location.href);
    if (
      !["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password
    )
      return null;
    return url;
  } catch {
    return null;
  }
};

const readTrustedSites = (): string[] => {
  try {
    const saved: unknown = JSON.parse(
      localStorage.getItem(TRUSTED_SITES_KEY) ?? "[]"
    );
    return Array.isArray(saved)
      ? saved.filter((host): host is string => typeof host === "string")
      : [];
  } catch {
    return [];
  }
};

export const rememberTrustedSite = (value: string): void => {
  const url = parseWebsiteUrl(value);
  if (!url || isBlockedSite(url.hostname)) return;
  try {
    localStorage.setItem(
      TRUSTED_SITES_KEY,
      JSON.stringify([...new Set([...readTrustedSites(), url.hostname])])
    );
  } catch {
    // Opening once still works when the browser disallows persistent storage.
  }
};

export const isBlockedSite = (hostname: string): boolean =>
  useLinkSafetyStore.getState().blockedHosts.includes(hostname);

export const needsLinkConfirmation = (url: URL): boolean => {
  if (isBlockedSite(url.hostname)) return true;
  if (url.origin === window.location.origin) return false;
  return (
    !useLinkSafetyStore.getState().trustedHosts.includes(url.hostname) &&
    !readTrustedSites().includes(url.hostname)
  );
};

export const openWebsite = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const openSafeLink = (value: string): void => {
  const url = parseWebsiteUrl(value);
  if (!url) return;
  if (needsLinkConfirmation(url)) {
    useLinkSafetyStore.setState({ pendingUrl: url.href });
    return;
  }
  openWebsite(url.href);
};

// These fields belong to public config, alongside (not inside) chat_config.
export const getLinkSafetyConfig = (config: any): string[] => {
  const publicConfig = {
    ...config,
    ...config?.platforms?.all,
    ...config?.platforms?.web,
  };
  const whitelist = Array.isArray(publicConfig.whitelist_dapps)
    ? publicConfig.whitelist_dapps
    : [];
  const dapps = Array.isArray(publicConfig.explore_dapps?.dapps)
    ? publicConfig.explore_dapps.dapps
    : [];
  return [
    ...new Set<string>(
      [...whitelist, ...dapps.map((dapp: any) => dapp?.url)].flatMap(
        (value: unknown) => {
          if (typeof value !== "string" || !value.trim()) return [];
          const url = parseWebsiteUrl(
            value.includes("://") ? value : `https://${value}`
          );
          return url ? [url.hostname] : [];
        }
      )
    ),
  ];
};
